import { useState, useEffect, useCallback, useRef } from 'react';
import type { User } from '@peles/shared';
import { isPositiveAmount, formatCurrency } from '@peles/shared';
import { useExpenseItem, useUpdateExpenseItem, useExpenseConfig } from '../../hooks/useExpenses';
import { useDialog } from '../../context/DialogContext';
import { extractApiErrorMessage } from '../../utils/apiError';
import { ExpenseDocumentsSection } from './ExpenseDocumentsSection';
import { LineMoneyPanel } from './money/LineMoneyPanel';
import { RecordPaymentSheet } from './money/RecordPaymentSheet';
import {
  NeedForm,
  EMPTY_EXPENSE_FORM,
  changedExpenseFields,
  expenseToFormState,
  readPrice,
  type ExpenseFormState,
} from './NeedForm';

interface ExpenseDetailPanelProps {
  expenseItemId: string;
  onClose: () => void;
  onUnsavedChange: (dirty: boolean) => void;
  currentUser: User;
  /** Show the line's payments and schedule; off where a Money tab already does. */
  showMoney?: boolean;
}

export function ExpenseDetailPanel({
  expenseItemId,
  onClose,
  onUnsavedChange,
  currentUser: _currentUser,
  showMoney = true,
}: ExpenseDetailPanelProps) {
  // currentUser is available for future permission checks
  void _currentUser;
  const { data: item, isLoading } = useExpenseItem(expenseItemId);
  const { data: config } = useExpenseConfig();
  const updateExpenseItem = useUpdateExpenseItem();
  const { confirm, alert } = useDialog();
  const [form, setForm] = useState<ExpenseFormState>(EMPTY_EXPENSE_FORM);
  const [originalForm, setOriginalForm] = useState<ExpenseFormState>(EMPTY_EXPENSE_FORM);
  const [saved, setSaved] = useState(false);
  // Chose the paid-in-full status with money still owed: record what is left.
  const [payRemaining, setPayRemaining] = useState(false);

  // Track dirty state
  const changed = changedExpenseFields(form, originalForm);
  const dirty = changed.length > 0;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Take the server's copy when it loads — and when it refetches, but only
  // while nothing has been typed. Adding a payment or coming back to the tab
  // refetches the expense, and that used to wipe an edit in progress.
  useEffect(() => {
    if (item && !dirtyRef.current) {
      const state = expenseToFormState(item);
      setForm(state);
      setOriginalForm(state);
      setSaved(false);
    }
  }, [item]);
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  const handleSave = useCallback(async () => {
    const updates: Record<string, unknown> = {};
    for (const key of changed) {
      if (key === 'price') {
        const price = readPrice(form.price);
        if (price === null || price === 'invalid') {
          void alert({
            title: 'Price needed',
            message: 'Enter the price as a number (0 if it is not known yet).',
            variant: 'warning',
          });
          return;
        }
        updates.price = price;
      } else if (key === 'budgetAmount') {
        const budget = readPrice(form.budgetAmount);
        if (budget === 'invalid') {
          void alert({
            title: 'Budget not understood',
            message: 'Enter the budget as a number, or leave it empty.',
            variant: 'warning',
          });
          return;
        }
        updates.budgetAmount = budget;
      } else if (key === 'vendorCategoryId' || key === 'statusId') {
        updates[key] = form[key];
      } else {
        // Every other field clears to null when emptied.
        updates[key] = form[key] || null;
      }
    }

    // The paid-in-full status chosen with money still owed is asked about
    // before anything is saved: the status must never stand beside an open
    // balance. Yes records what is left (the server then moves the line onto
    // that status itself); no saves nothing.
    const paidStatus = config?.statuses.find((st) => st.id === updates.statusId && st.isPaidInFull);
    const price = typeof updates.price === 'number' ? updates.price : (item?.price ?? 0);
    const owed = item ? Math.round((price - item.totalPaid) * 100) / 100 : 0;
    if (paidStatus && isPositiveAmount(owed)) {
      // Other edits are saved first and stand on their own, so say so: a
      // payment sheet closed without paying leaves them saved and the status
      // as it was.
      const savesOthers = Object.keys(updates).some((key) => key !== 'statusId');
      const yes = await confirm({
        title: 'Record the rest as paid?',
        message:
          `${formatCurrency(owed)} is still open on this line. Record the remaining ${formatCurrency(owed)} as paid? The line moves to ${paidStatus.name} once the payment is recorded.` +
          (savesOthers
            ? ` If you continue, your other changes will be saved before the payment sheet opens. If you close the sheet without recording a payment, those changes will stay saved and the status will not change.`
            : ''),
        confirmLabel: savesOthers ? 'Save and record payment' : 'Record payment',
        variant: 'info',
      });
      if (!yes) return;
      delete updates.statusId;
      setForm((f) => ({ ...f, statusId: originalForm.statusId }));
      if (Object.keys(updates).length === 0) {
        setPayRemaining(true);
        return;
      }
    }
    const thenPay = paidStatus !== undefined && isPositiveAmount(owed);

    updateExpenseItem.mutate(
      { id: expenseItemId, updates },
      {
        onSuccess: (updated) => {
          const state = expenseToFormState(updated);
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
          if (thenPay) setPayRemaining(true);
        },
        onError: (err) =>
          void alert({
            title: 'Not saved',
            message: extractApiErrorMessage(err, 'The expense could not be saved.'),
            variant: 'error',
          }),
      },
    );
  }, [form, changed, expenseItemId, updateExpenseItem, alert, confirm, config, item, originalForm]);

  if (isLoading) {
    return <p className="text-gray-500 text-sm">Loading...</p>;
  }

  if (!item) {
    return <p className="text-gray-500 text-sm">Expense item not found</p>;
  }

  return (
    <div>
      {saved && (
        <div role="status" className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      <NeedForm
        value={form}
        onChange={setForm}
        idPrefix="detail-expense"
        currentCategoryName={item.vendorCategoryName}
        sourcing={item.phase === 'SOURCING'}
        onError={(message) =>
          void alert({ title: 'Something went wrong', message, variant: 'error' })
        }
      />

      <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || updateExpenseItem.isPending}
          className="min-h-11 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateExpenseItem.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Payments — a need still being sourced has none to take. */}
      {showMoney && item.phase !== 'SOURCING' && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <LineMoneyPanel item={item} />
        </div>
      )}
      <RecordPaymentSheet
        item={payRemaining ? item : null}
        start="remaining"
        onClose={() => setPayRemaining(false)}
      />

      {/* Documents Section */}
      <ExpenseDocumentsSection expenseItemId={expenseItemId} />
    </div>
  );
}
